export interface AuthUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  role?: string;
}

type ProfileRoleFetchMode = 'single' | 'maybeSingle';

export interface ProfileRoleResult {
  data?: {
    role?: string | null;
  } | null;
  error?: any;
}

interface ResolveProfileRoleOptions {
  supabase: any;
  userId: string;
  fallbackRole: string;
  fetchMode?: ProfileRoleFetchMode;
  timeoutMs?: number;
  shouldUseResult?: () => boolean;
  onProfileError?: (error: any) => void;
  onFetchError?: (error: any) => void;
  onRoleFound?: (role: string) => void;
}

interface SupabaseUserLike {
  id: string;
  email?: string | null;
  user_metadata?: {
    first_name?: string;
    last_name?: string;
    username?: string;
    role?: string;
  } | null;
}

export const getSupabaseUserMetadataRole = (user: SupabaseUserLike): string => {
  return user.user_metadata?.role || 'user';
};

export const buildAuthUserFromSupabaseUser = (
  user: SupabaseUserLike,
  role: string = getSupabaseUserMetadataRole(user)
): AuthUser => ({
  id: user.id,
  email: user.email || '',
  first_name: user.user_metadata?.first_name,
  last_name: user.user_metadata?.last_name,
  username: user.user_metadata?.username,
  role,
});

export const resolveProfileRole = async ({
  supabase,
  userId,
  fallbackRole,
  fetchMode = 'maybeSingle',
  timeoutMs = 3000,
  shouldUseResult,
  onProfileError,
  onFetchError,
  onRoleFound,
}: ResolveProfileRoleOptions): Promise<string> => {
  try {
    const profileQuery = supabase
      .from('profiles')
      .select('role')
      .eq('id', userId);
    const profilePromise: Promise<ProfileRoleResult> =
      fetchMode === 'single' ? profileQuery.single() : profileQuery.maybeSingle();
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Profile fetch timeout')), timeoutMs);
    });

    const { data: profile, error: profileError } = await Promise.race([profilePromise, timeoutPromise]);

    if (shouldUseResult && !shouldUseResult()) {
      return fallbackRole;
    }

    if (profileError) {
      onProfileError?.(profileError);
      return fallbackRole;
    }

    if (profile?.role) {
      onRoleFound?.(profile.role);
      return profile.role;
    }
  } catch (profileError: any) {
    if (!shouldUseResult || shouldUseResult()) {
      onFetchError?.(profileError);
    }
  }

  return fallbackRole;
};
